import React, { type ReactElement, useCallback, useMemo, useState } from 'react';
import { useCombobox } from 'downshift';
import ButtonSeperator from 'atom-components/combo-boxes/shared/button-seperator';
import ButtonsContainer from 'atom-components/combo-boxes/shared/buttons-container';
import ClearSelectionButton from 'atom-components/combo-boxes/shared/clear-selection-button';
import ComboBox from 'atom-components/combo-boxes/shared/combo-box';
import ItemInput from 'atom-components/combo-boxes/shared/item-input';
import ItemInputContainer from 'atom-components/combo-boxes/shared/item-input-container';
import Menu from 'atom-components/combo-boxes/shared/menu';
import MenuItem from 'atom-components/combo-boxes/shared/menu-item';
import ToggleMenuButton from 'atom-components/combo-boxes/shared/toggle-menu-button';
import type { ComboBoxItem, SharedComboBoxProps } from 'atom-components/combo-boxes/types';

type Props = SharedComboBoxProps & {
  /* This is a controlled combo box, so we must pass the select item state and
    state setter as props. The parent component is responsible for managing
    these. */
  selectedItems: ComboBoxItem[];
  setSelectedItems: (items: ComboBoxItem[]) => void;

  // onChange is required since it sets state in the outer uncontrolled component.
  onChange: (items: ComboBoxItem[]) => void;
};

/* This component is based on the example at:
   https://www.downshift-js.com/use-combobox

   MultiComboBox can be used to select multiple items (and deselect them) with the option to search
   for the name of the item.

   This is a controlled component which does not manage the state of selectedItems (however it does
   manage the input state).
*/
export default function ControlledMultiComboBox({
  id,
  allItems,
  selectedItems,
  setSelectedItems,
  placeholder,
  className,
  hasError,
  clearable,
  disabled,
  onBlur,
  onChange
}: Props): ReactElement {
  const getFilteredItems = useCallback(
    (inputValue: string) => {
      const lowerCasedInputValue = inputValue.toLowerCase();

      return allItems.filter((item) => item.name.toLowerCase().includes(lowerCasedInputValue));
    },
    [allItems]
  );

  const [inputValue, setInputValue] = useState('');

  const items = useMemo(() => getFilteredItems(inputValue), [getFilteredItems, inputValue]);

  const updateSelectedItems = (newSelectedItems: ComboBoxItem[]): void => {
    // Check that the new array of selected items has changed to avoid adding the same item twice.
    if (newSelectedItems !== selectedItems) {
      setSelectedItems(newSelectedItems);
      onChange(newSelectedItems);

      // Unlike the single combo box, here we're clearing the input value after an item is added or
      // removed, to avoid the input become cluttered. Selected items are highlighted in the list
      // and can be displayed in an external "applied filters" list below the filter form.
      setInputValue('');
    }
  };

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps
  } = useCombobox({
    items,
    itemToString(item) {
      return item ? item.name : '';
    },
    defaultHighlightedIndex: 0, // After selection, highlight the first item.
    selectedItem: null,
    stateReducer(_state, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            ...(changes.selectedItem && {
              isOpen: false,
              highlightedIndex: 0
            })
          };
        default:
          return changes;
      }
    },
    onStateChange({ inputValue: newInputValue, type, selectedItem: newSelectedItem }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (newSelectedItem) {
            if (selectedItems.find((si) => si.id === newSelectedItem.id)) {
              // Remove item from selected items:
              updateSelectedItems(selectedItems.filter((si) => si.id !== newSelectedItem.id));
            } else {
              // Add item to selected items:
              updateSelectedItems([...selectedItems, newSelectedItem]);
            }
          }
          break;
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(newInputValue || '');
          break;
        default:
          break;
      }
    }
  });

  const isClearable = selectedItems.length > 0 && clearable;

  return (
    <ComboBox className={className}>
      <ItemInputContainer>
        <ItemInput
          placeholder={placeholder}
          disabled={disabled}
          hasError={hasError}
          getInputProps={getInputProps}
          id={id || getInputProps().id}
          onBlur={onBlur}
          // Ensures that the input value is cleared as a soon as an item is selected/deselected,
          // but is populated by getInputProps while the user is typing in a search string:
          isValueFromInputProps={inputValue !== ''}
        />
        <ButtonsContainer
          allowSpaceForClearButton={isClearable || false}
          allowSpaceForSelectedItemCount={isClearable || false}
        >
          {selectedItems && isClearable && (
            <ClearSelectionButton
              selectedItemCount={selectedItems.length}
              onClick={(): void => {
                updateSelectedItems([]);
              }}
            />
          )}
          <ButtonSeperator />
          <ToggleMenuButton disabled={disabled} getToggleButtonProps={getToggleButtonProps} />
        </ButtonsContainer>
      </ItemInputContainer>
      <Menu availableItems={items} isOpen={isOpen} getMenuProps={getMenuProps}>
        {isOpen &&
          items.map((item, index) => (
            <MenuItem
              key={item.id}
              isSelected={selectedItems.find((si) => si.id === item.id) !== undefined}
              isHighlighted={
                !selectedItems.find((si) => si.id === item.id) && highlightedIndex === index
              }
              item={item}
              index={index}
              getItemProps={getItemProps}
            />
          ))}
      </Menu>
    </ComboBox>
  );
}
